import AuthLayout from "@/components/layout/AuthLayout";
import { createBrowserRouter, Navigate, Outlet, redirect, RouterProvider, useLoaderData, useNavigate, useNavigation } from "react-router";
import LoginPage from '@/pages/admin/login';
import SignupPage from '@/pages/admin/signup';
import { getUser, useGetUser } from "@/services/generatedAPIs";
import { UserInfoResponse, UserInfoResponseRole } from "@/services/generatedAPIs.schemas";
import { PAGE, ROUTE } from "./constants";
import Dashboard from "@/pages/Dashboard";
import { useAuth } from "@/contexts/AuthContext";
import HomeworkPage from "@/pages/teacher/HomeworkPage";
import AdminPage from "@/pages/teacher/AdminPage";
import HomePage from "@/pages/HomePage";
import LibraryPage from "@/pages/teacher/LibraryPage";
import TopicEditPage from "@/pages/teacher/TopicEditPage";
import QuestionEditPage from "@/pages/teacher/QuestionEditPage";
import AssignmentPage from "@/pages/teacher/AssignmentPage";
import ManualCompileAssignmentPage from "@/pages/teacher/ManualCompileAssignmentPage";
import AICompileAssignmentPage from "@/pages/teacher/AICompileAssignmentPage";
import OverviewPage from "@/pages/teacher/OverviewPage";
import { default as StudentOverviewPage } from "@/pages/student/OverviewPage";
import { default as StudentHomeworkPage } from "@/pages/student/HomeworkPage";
import AITutorPage from "@/pages/student/AITutorPage";
import ClassViewPage from "@/pages/teacher/ClassViewPage";
import StudentViewPage from "@/pages/teacher/StudentViewPage";
import PracticeHomePage from "@/pages/student/PracticeHomePage";
import PracticeEditPage from "@/pages/student/PracticeEditPage";
import AICompileAssignmentResultPage from "@/pages/teacher/AICompileAssignmentResultPage";
import LibraryImportPage from "@/pages/teacher/LibraryImportPage";
import PrivacyPolicy from "@/pages/admin/privacy-policy";
import StudentProfilePage from "@/pages/student/ProfilePage";
import SelfAssessPage from "@/pages/student/SelfAssessPage";
import RequestResetPasswordPage from "@/pages/admin/request-reset-password";
import ResetPasswordPage from "@/pages/admin/reset-password";
import PracticeClassView from "@/pages/teacher/PracticeClassViewPage";
import GamificationConfigPage from "@/pages/teacher/GamificationConfigPage";
import GamificationRecordsPage from "@/pages/teacher/GamificationRecordsPage";
import GamificationLeaderboardsPage from "@/pages/teacher/GamificationLeaderboardsPage";
import { ReleaseNotesDialog } from "@/components/ReleaseNotesDialog";
import AccessRestrictedPage from "@/pages/student/access-restricted";
import ExpertAnnotationPage from "@/pages/expert/ExpertAnnotationPage";
import ExpertAdminPage from "@/pages/expert/ExpertAdminPage";
import AboutUsV2Page from "@/pages/admin/about-us-v2";
import AboutUsPage from "@/pages/admin/about-us";




const roleChecker = (allowedRoles: UserInfoResponseRole[]) => {
  return allowedRoles;
}

const ProtectedRoute = () => {
  const { getCurrentUser } = useAuth();
  const user = getCurrentUser();
  if (!user) {
    return <Navigate to={ROUTE.LOGIN} replace />; // 无权限则跳转到无权限页
  }

  const userRole = user.role;
  const isAllowed = useLoaderData().includes(userRole); // 检查用户角色是否在允许的角色列表中
  if (!isAllowed) {
    return <Navigate to="/unauthorized" replace />; // 无权限则跳转到无权限页
  }

  return <Outlet />; // 允许访问子路由
};

const router = createBrowserRouter([
  {
    path: "/",
    Component: RootRouter,
    children: [
      { index: true, Component: HomePage },
      {
        path: PAGE.TEACHER, Component: ProtectedRoute, loader: () => { return roleChecker([UserInfoResponseRole.TEACHER]); }, children: [
          {
            path: PAGE.DASHBOARD, Component: Dashboard, children: [
              { path: PAGE.OVERVIEW, Component: HomeworkPage },
            ]
          },
          {
            path: PAGE.CLASS, Component: Dashboard, children: [
              { path: PAGE.ADMIN, Component: AdminPage },
            ]
          },
          {
            path: PAGE.LIBRARY, Component: Dashboard, children: [
              { path: PAGE.EDIT, Component: LibraryPage },
              { path: PAGE.TOPIC_EDIT, Component: TopicEditPage },
              { path: PAGE.QUESTION_EDIT, Component: QuestionEditPage },
              { path: PAGE.LIBRARY_IMPORT, Component: LibraryImportPage },
            ]
          },
          {
            path: PAGE.HOMEWORK, Component: Dashboard, children: [
              { path: PAGE.ASSIGN, Component: AssignmentPage },
              { path: PAGE.MANUAL_EDIT, Component: ManualCompileAssignmentPage },
              { path: PAGE.AIEDIT, Component: AICompileAssignmentPage },
              { path: PAGE.AIEDIT_RESULT, Component: AICompileAssignmentResultPage },
              { path: PAGE.OVERVIEW, Component: OverviewPage },
              { path: PAGE.CLASSVIEW, Component: ClassViewPage },
              { path: PAGE.STUDENTVIEW, Component: StudentViewPage },
            ]
          },
          {
            path: PAGE.PRACTICE, Component: Dashboard, children: [
              { path: PAGE.CLASSVIEW, Component: PracticeClassView },
            ]
          },
          {
            path: PAGE.GAMIFICATION, Component: Dashboard, children: [
              { path: PAGE.CONFIG, Component: GamificationConfigPage },
              { path: PAGE.RECORDS, Component: GamificationRecordsPage },
              { path: PAGE.LEADERBOARDS, Component: GamificationLeaderboardsPage },
            ]
          },
          {
            path: PAGE.EXPERT, Component: Dashboard, children: [
              { path: PAGE.ANNOTATION, Component: ExpertAnnotationPage },
              { path: PAGE.ADMIN, Component: ExpertAdminPage },
            ]
            
          },
        ]
      },
      {
        path: PAGE.STUDENT, Component: ProtectedRoute, loader: () => { return roleChecker([UserInfoResponseRole.STUDENT]); }, children: [
          {
            path: PAGE.DASHBOARD, Component: Dashboard, children: [
              { path: PAGE.OVERVIEW, Component: StudentOverviewPage },
            ]
          },
          {
            path: PAGE.HOMEWORK, Component: Dashboard, children: [
              { path: PAGE.EDIT, Component: StudentHomeworkPage },
            ]
          },
          {
            path: PAGE.PRACTICE, Component: Dashboard, children: [
              { path: PAGE.HOME, Component: PracticeHomePage },
              { path: PAGE.EDIT, Component: PracticeEditPage },
            ]
          },
          {
            path: PAGE.AI_BOT, Component: Dashboard, children: [
              { path: PAGE.CHAT, Component: AITutorPage },
            ]
          },
          {
            path: PAGE.SETTING, Component: Dashboard, children: [
              { path: PAGE.PROFILE, Component: StudentProfilePage },
            ]
          },
          {
            path: PAGE.SELF_ASSESS, Component: Dashboard, children: [
              { path: PAGE.HOME, Component: SelfAssessPage },
            ]
          },
        ]
      },
      {
        path: "auth/",
        Component: AuthLayout,
        children: [
          { path: PAGE.LOGIN, Component: LoginPage },
          { path: PAGE.SIGNUP, Component: SignupPage },
          { path: PAGE.REQUEST_RESET_PASSWORD, Component: RequestResetPasswordPage },
          { path: PAGE.RESET_PASSWORD, Component: ResetPasswordPage },
          { path: PAGE.PRIVACY_POLICY, Component: PrivacyPolicy },
          { path: PAGE.ABOUT_V2, Component: AboutUsV2Page },
          { path: PAGE.ABOUT, Component: AboutUsPage },
          { path: PAGE.ACCESS_RESTRICTED, Component: AccessRestrictedPage },
        ],
      },
    ],
  },
]);

export function RootRouter() {
  const navigation = useNavigation();
  const isNavigating = Boolean(navigation.location);
  return (
    <div className="h-full w-full flex flex-col">
      {isNavigating ? <div>loading...</div> : <Outlet />}
      <ReleaseNotesDialog />
    </div>
  );
}

export function RouterProviderWithAuth() {
  return (
    <RouterProvider router={router} />
  );
}
